/**
 * CustomInputBar — Fixed-bottom text input with mic toggle for session.
 */
import { useState, useRef } from 'react';
import { Mic, MicOff } from 'lucide-react';

export default function CustomInputBar({
  onSubmit,
  placeholder = 'Type what you want to do...',
  micOn = false,
  onMicToggle,
  actions,
}) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setText('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-100 px-4 py-3 pb-safe z-40">
      {/* Action row (Home / New task etc.) when present */}
      {actions && (
        <div className="flex items-center justify-between gap-2 mb-2">
          {actions}
        </div>
      )}

      {/* Input row */}
      <div className="flex items-end gap-2">
        {/* Mic toggle */}
        {onMicToggle && (
          <button
            onClick={onMicToggle}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
              micOn
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {micOn
              ? <Mic size={18} strokeWidth={2} />
              : <MicOff size={18} strokeWidth={2} />
            }
          </button>
        )}

        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'; }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          className="flex-1 px-4 py-2.5 rounded-2xl bg-gray-100 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-blue-200 resize-none leading-snug"
          style={{ maxHeight: 96, overflow: 'auto' }}
        />

        <button
          onClick={handleSubmit}
          disabled={!text.trim()}
          className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center disabled:bg-gray-200 transition-colors"
        >
          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
